import React, { useEffect, useState } from "react";
import styles from "./index.module.css";
import DfProductCard from "../DfProductCard";
import DropDown from "../Dropdown";
import { useDispatch } from "react-redux";
import { deleteProductAsyncThunk } from "../../../store/slices/watchlist/data.slice";
import { Source, locationMapping } from "../../../utils/constant";
import { useSelector } from "react-redux";
import BeatLoader from "react-spinners/BeatLoader";
import PulseLoader from "react-spinners/PulseLoader";
import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import PdfDocument from "../../layouts/PdfDocument";
import { convert_usd_to_nzd } from "../../../utils/conversion";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const WatchlistCategory = ({
  children,
  deleteTab,
  products,
  watchlist_id,
  setIsEdit,
  onProductDeleted,
  sorting,
  onSortingChange,
  sortingSteps,
}) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((store) => store.watchlist);
  const { source, currency } = useSelector((store) => store.constant);
  const [localProducts, setLocalProducts] = useState(products);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    setLocalProducts(products);
  }, [products]);

  const deleteProduct = (product_id, source) => {
    if (!loading) {
      setLocalProducts((prevProducts) =>
        prevProducts.filter(
          (product) => product.products_data[0].product_id !== product_id
        )
      );

      dispatch(
        deleteProductAsyncThunk({
          watchlist_id,
          product_id,
          source,
        })
      ).then(() => {
        onProductDeleted();
      });
    }
  };

  const formatWatchlistDataForPDF = () => {
    if (!localProducts || localProducts.length === 0) {
      return [];
    }

    const liquorProducts = localProducts.filter(
      (item) => item.products_data && item.products_data[0] && item.products_data[0].category === "liquor"
    );

    const beautyProducts = localProducts.filter(
      (item) => item.products_data && item.products_data[0] && item.products_data[0].category === "beauty"
    );

    const getItems = (category) => {
      const products = category === "liquor" ? liquorProducts : beautyProducts;

      let items = [];

      products.forEach((product, index) => {
        const sourceProduct = product.products_data.find(
          (item) => item.website === source
        );
        
        if (!sourceProduct) return;

        let compareProductsData = [];
        let priceSumOfAllCompareProduct = 0;

        product.products_data.forEach((item) => {
          const compareDifference = parseFloat(convert_usd_to_nzd(
            sourceProduct.latest_price - item.latest_price,
            currency
          ));

          let compareProduct = {
            to:
              locationMapping[item.website].location +
              "-" +
              locationMapping[item.website].brand,
            item: item.title,
            price: "$" + convert_usd_to_nzd(item.latest_price, currency, item),
            difference: `${compareDifference >= 0 ? "$" : "-$"}${Math.abs(
              Math.round(compareDifference * 100) / 100
            )}`,
            unit: item.unit,
            qty: item.qty,
          };
          compareProductsData.push(compareProduct);
          
          // Only add to sum if it's not the source product
          if (item.website !== source) {
            priceSumOfAllCompareProduct += Number(item.latest_price);
          }
        });

        // Calculate comparable price only from comparison products (excluding source)
        const comparisonProducts = product.products_data.filter(item => item.website !== source);
        let comparablePrice = comparisonProducts.length > 0 
          ? Math.round(parseFloat(convert_usd_to_nzd(priceSumOfAllCompareProduct / comparisonProducts.length, currency)) * 100) / 100
          : 0;
        
        // Calculate difference and percentage
        let sourcePrice = parseFloat(convert_usd_to_nzd(sourceProduct.latest_price, currency, sourceProduct));
        let difference = Math.round((sourcePrice - comparablePrice) * 100) / 100;
        let percentage = comparablePrice > 0 
          ? Math.round((difference / sourcePrice) * 100 * 100) / 100
          : 0;

        // Color coding logic for duty-free reports: 0 or negative = green, 0-15 = yellow, >15 = red
        let category;
        if (percentage <= 0) {
          category = "positive"; // Green for 0 or negative percentages (competitive)
        } else if (percentage > 0 && percentage <= 15) {
          category = "watch"; // Yellow for 0-15% (moderately expensive)
        } else {
          category = "negative"; // Red for > 15% (very expensive)
        }

        let temp = {
          itemNo: index + 1,
          itemType: locationMapping[sourceProduct.website].type,
          sku: sourceProduct.sku ?? "",
          description: sourceProduct.title,
          price: "$" + convert_usd_to_nzd(sourceProduct.latest_price, currency, sourceProduct),
          compare: compareProductsData,
          comparablePrice: `${comparablePrice >= 0 ? "$" : "-$"}${Math.abs(comparablePrice)}`,
          difference: `${difference >= 0 ? "$" : "-$"}${Math.abs(difference)}`,
          percentage: percentage.toFixed(2) + "%",
          category: category,
          brand: sourceProduct.brand || "N/A",
          productCategory: sourceProduct.category || "N/A",
          priceRankVsComparative: sourceProduct.pricerank || "-",
          onPromo: "-",
          avg90DayChangeInPrice: "-",
        };

        items.push(temp);
      });

      return items;
    };

    return [
      {
        name: "Liquor",
        location: locationMapping[source].location,
        items: getItems("liquor"),
      },
      {
        name: "Beauty",
        location: locationMapping[source].location,
        items: getItems("beauty"),
      },
    ];
  };

  // Helper function to get source website name dynamically
  const getSourceWebsiteName = () => {
    const sourceInfo = locationMapping[source];
    if (sourceInfo) {
      return `${sourceInfo.location} - ${sourceInfo.brand}`;
    }
    return "Unknown Source";
  };

  // Helper function to get normalized source name for filtering
  const getNormalizedSourceName = () => {
    const sourceInfo = locationMapping[source];
    if (sourceInfo) {
      return `${sourceInfo.location}-${sourceInfo.brand}`;
    }
    return "";
  };

  const handleExport = async () => {
    if (!localProducts || localProducts.length === 0) {
      toast.error("No products to export.");
      return;
    }

    setExportLoading(true);
    try {
      const data = formatWatchlistDataForPDF();
      
      // Check if we have any data to export
      const hasData = data.some(category => category.items && category.items.length > 0);
      if (!hasData) {
        toast.error("No valid products to export.");
        return;
      }

      const doc = <PdfDocument data={data} section="df" />;
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Watchlist_${children}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Watchlist exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export watchlist.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!localProducts || localProducts.length === 0) {
      toast.error("No products to export.");
      return;
    }

    setExportLoading(true);
    try {
      const data = formatWatchlistDataForPDF();
      
      // Check if we have any data to export
      const hasData = data.some(category => category.items && category.items.length > 0);
      if (!hasData) {
        toast.error("No valid products to export.");
        return;
      }

      // Combine all items from both categories
      const allItems = [...(data[0]?.items || []), ...(data[1]?.items || [])];
      if (allItems.length === 0) {
        toast.error("No data to export.");
        return;
      }

      // Helper to normalize website labels for comparison
      const normalize = (str) =>
        (str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const SOURCE_WEBSITE = source;
      const normalizedSource = normalize(SOURCE_WEBSITE);
      const allWebsites = new Set();
      allItems.forEach((item) => {
        (item.compare || []).forEach((cmp) => {
          if (cmp.to && normalize(cmp.to) !== normalizedSource)
            allWebsites.add(cmp.to);
        });
      });
      const websiteList = Array.from(allWebsites)?.filter(
        (item) => item !== getNormalizedSourceName()
      );

      // Map websites to tags (duty-free/domestic)
      const websiteTagMap = {};
      Object.keys(locationMapping).forEach((key) => {
        websiteTagMap[
          locationMapping[key].location + "-" + locationMapping[key].brand
        ] = locationMapping[key].type?.toLowerCase();
      });

      // Group websites
      const dutyFreeWebsites = websiteList.filter((site) =>
        (websiteTagMap[site] || "").includes("duty")
      );
      const domesticWebsites = websiteList.filter((site) =>
        (websiteTagMap[site] || "").includes("domestic")
      );

      // --- Build multi-row header (same format as DF Reporting Excel) ---
      const sourceCols = 8;
      const perSiteCols = 4;
      const sourceWebsiteName =
        locationMapping[source]?.location || getSourceWebsiteName();
      const row1 = [
        ...Array(sourceCols).fill(""),
        ...Array(dutyFreeWebsites.length * perSiteCols).fill(""),
        ...Array(domesticWebsites.length * perSiteCols).fill(""),
      ];
      if (dutyFreeWebsites.length > 0) row1[sourceCols] = "Duty Free";
      if (domesticWebsites.length > 0)
        row1[sourceCols + dutyFreeWebsites.length * perSiteCols] = "Domestic";
      const row2 = [
        sourceWebsiteName,
        ...Array(sourceCols - 1).fill(""),
        ...dutyFreeWebsites.flatMap((site) => [site, ...Array(perSiteCols - 1).fill("")]),
        ...domesticWebsites.flatMap((site) => [site, ...Array(perSiteCols - 1).fill("")]),
      ];
      const row3 = [
        "SKU",
        `${sourceWebsiteName} title`,
        `${sourceWebsiteName} brand`,
        `${sourceWebsiteName} category`,
        "Price Rank vs Comparative Retailer",
        "On promo",
        "Avg 90 days change in price ($)",
        `${sourceWebsiteName} price`,
        ...[...dutyFreeWebsites, ...domesticWebsites].flatMap(() => [
          "Price",
          "Diff %",
          "Diff $",
          "Volume",
        ]),
      ];

      // Data rows
      const rows = allItems.map((item) => {
        const row = [
          item.sku ?? "-",
          item.description || "-",
          item.brand || "-",
          item.productCategory || "-",
          item.priceRankVsComparative ?? "-",
          item.onPromo ?? "-",
          item.avg90DayChangeInPrice ?? "-",
          item.price || "-",
        ];
        [...dutyFreeWebsites, ...domesticWebsites].forEach((site) => {
          const cmp = (item.compare || []).find((c) => c.to === site);
          row.push(cmp ? cmp.price : "-");
          // Calculate diff %: ((source price - website price)/website price)
          let sourcePrice = parseFloat(
            (item.price || "").replace(/[^\d.\-]/g, "")
          );
          let websitePrice = cmp
            ? parseFloat((cmp.price || "").replace(/[^\d.\-]/g, ""))
            : NaN;
          if (
            cmp &&
            !isNaN(sourcePrice) &&
            !isNaN(websitePrice) &&
            websitePrice !== 0
          ) {
            let diff = ((sourcePrice - websitePrice) / websitePrice) * 100;
            row.push(diff.toFixed(2) + "%");
          } else {
            row.push("-");
          }
          row.push(cmp && cmp.difference != null ? cmp.difference : "-");
          // Add volume information
          const volume = cmp?.qty && cmp?.unit ? cmp?.qty + " " + cmp?.unit : "-";
          row.push(volume);
        });
        return row;
      });

      // Build worksheet data
      const ws_data = [row1, row2, row3, ...rows];

      // Merges
      const merges = [];
      if (dutyFreeWebsites.length > 0) {
        merges.push({
          s: { r: 0, c: sourceCols },
          e: { r: 0, c: sourceCols + dutyFreeWebsites.length * perSiteCols - 1 },
        });
      }
      if (domesticWebsites.length > 0) {
        merges.push({
          s: { r: 0, c: sourceCols + dutyFreeWebsites.length * perSiteCols },
          e: {
            r: 0,
            c: sourceCols + dutyFreeWebsites.length * perSiteCols + domesticWebsites.length * perSiteCols - 1,
          },
        });
      }
      // Merge each website name in row 2 across its four columns
      [...dutyFreeWebsites, ...domesticWebsites].forEach((site, idx) => {
        const col = sourceCols + idx * perSiteCols;
        merges.push({ s: { r: 1, c: col }, e: { r: 1, c: col + perSiteCols - 1 } });
      });

      // Create worksheet and workbook
      const ws = XLSX.utils.aoa_to_sheet(ws_data);
      ws["!merges"] = merges;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Watchlist");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([wbout], { type: "application/octet-stream" }),
        `Watchlist_${children}.xlsx`
      );
      toast.success("Excel downloaded!");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel file.");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className={styles["container"]}>
      <div className={styles["store_head"]}>
        <div className={styles["store_title"]}>
          <div>
            <span>{children}</span>
            <div>
              <i class="bi bi-pen" onClick={() => setIsEdit(true)}></i>
              <i class="bi bi-trash" onClick={deleteTab}></i>
            </div>
          </div>
          <div>Showing {localProducts && localProducts.length} products</div>
        </div>
        <div className={styles["store_filters"]}>
          <div className={styles["filter"]}>
            <div>
              <div>Sort By</div>
              <div>
                <i class="bi bi-funnel"></i>
              </div>
            </div>
            <div className={styles["dropdown"]}>
              <DropDown
                steps={sortingSteps}
                selectedValue={sorting}
                onChange={onSortingChange}
              />
            </div>
          </div>
          <div className={styles["export_buttons"]}>
            <div 
              className={`${styles["export"]} ${(!localProducts || localProducts.length === 0) ? styles["export_disabled"] : ""}`} 
              onClick={(!localProducts || localProducts.length === 0) ? undefined : handleExport}
            >
              <span>{exportLoading ? "Exporting..." : "Download PDF"}</span>
              <i class="bi bi-download"></i>
            </div>
            <div 
              className={`${styles["export"]} ${(!localProducts || localProducts.length === 0) ? styles["export_disabled"] : ""}`} 
              onClick={(!localProducts || localProducts.length === 0) ? undefined : handleDownloadExcel}
            >
              <span>{exportLoading ? "Exporting..." : "Download Excel"}</span>
              <i class="bi bi-file-earmark-excel"></i>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div>
          {" "}
          <PulseLoader />{" "}
        </div>
      ) : (
        localProducts &&
        localProducts?.map((product, index) => (
          <div className={styles["store_product"]} key={index}>
            <DfProductCard
              {...product}
              type={"watchlist"}
              deleteProduct={deleteProduct}
              productIdx={index}
              source={source}
              currency={currency}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default WatchlistCategory;
